import { supabase } from "@/supabase";
import { storage_url_render } from "../utils/general";

// Stars Magnet talent recruitment (Management › Stars Magnet).
// Rows are every Brandripplr influencer (~114k), so the list is filtered, sorted
// and paged in the database: sm_talent_page(), sm_talent_counts() and
// sm_filter_options(). Stats are refreshed nightly and hourly for changed talents;
// status, lead and notes are live and written only through the sm_* RPCs
// (see the stars_magnet_* migrations).
export const SM_AGENCY_ID = 565;

const err = (e) => (e ? e.message || String(e) : null);
const fullName = (u) => [u?.first_name, u?.last_name].filter(Boolean).join(" ").trim() || u?.email || "Unknown";
const avatarUrl = (path) => {
  if (!path) return null;
  if (/^https?:/i.test(path)) return path;
  return storage_url_render + path + "?width=96&height=96&quality=75&resize=cover";
};

const shapeTalent = (t) => ({
  ...t,
  avatar: avatarUrl(t.avatar),
  channels: (t.channels || []).map((c) => ({
    ...c,
    username: (c.username || "").replace(/^@/, ""),
    followers: c.followers == null ? null : Number(c.followers),
    er: c.er == null ? null : Number(c.er),
  })),
  max_followers: t.max_followers == null ? null : Number(t.max_followers),
  audience_sa: t.audience_sa == null ? null : Number(t.audience_sa),
  audience_ae: t.audience_ae == null ? null : Number(t.audience_ae),
  avg_reel_fee: t.avg_reel_fee == null ? null : Number(t.avg_reel_fee),
  top_client: (t.clients || [])[0] || null,
});

export const StarsMagnetService = {
  // ------------------------------------------------------------------ access
  async getMyAccess() {
    const [m, a] = await Promise.all([supabase.rpc("sm_is_member"), supabase.rpc("sm_is_admin")]);
    if (m.error) return { member: false, admin: false, error: err(m.error) };
    return { member: !!m.data, admin: !!a.data, error: null };
  },

  // ----------------------------------------------------------------- talents
  // One sorted page of talents for the filters, plus the total.
  async getPage(filters, { sort = "completed_campaigns", desc = true, offset = 0, limit = 50 } = {}) {
    const { data, error } = await supabase.rpc("sm_talent_page", {
      p_filters: filters,
      p_sort: sort,
      p_desc: desc,
      p_offset: offset,
      p_limit: limit,
    });
    if (error) return { rows: [], total: 0, error: err(error) };
    return { rows: (data?.rows || []).map(shapeTalent), total: data?.total || 0, error: null };
  },
  // Pipeline tiles, lead counts and status / reason totals.
  async getCounts(filters) {
    const { data, error } = await supabase.rpc("sm_talent_counts", { p_filters: filters });
    return { counts: data || null, error: err(error) };
  },
  async getFilterOptions() {
    const { data, error } = await supabase.rpc("sm_filter_options");
    return { options: data || { countries: [], agencies: [], platforms: [] }, error: err(error) };
  },
  async getTalent(influencerId) {
    const { data, error } = await supabase.rpc("sm_talents", { p_influencer_id: influencerId });
    if (error) return { talent: null, error: err(error) };
    return { talent: data && data[0] ? shapeTalent(data[0]) : null, error: null };
  },
  async getEvents(influencerId) {
    const { data, error } = await supabase.rpc("sm_talent_events", { p_influencer_id: influencerId });
    return { events: data || [], error: err(error) };
  },

  // ------------------------------------------------------------------ writes
  async setStatus(influencerIds, { status, date = null, reason = null, reasonOther = null, note = null, reachSource = null, reachBy = null, reachByName = null }) {
    const { data, error } = await supabase.rpc("sm_set_status", {
      p_influencer_ids: influencerIds,
      p_status: status,
      p_status_date: date,
      p_reason: reason,
      p_reason_other: reasonOther,
      p_note: note,
      p_reach_source: reachSource,
      p_reach_by: reachBy,
      p_reach_by_name: reachByName,
    });
    return { count: data || 0, error: err(error) };
  },
  async setLead(influencerIds, leadId) {
    const { data, error } = await supabase.rpc("sm_set_lead", { p_influencer_ids: influencerIds, p_lead_id: leadId || null });
    return { count: data || 0, error: err(error) };
  },
  async addNote(influencerId, note) {
    const { error } = await supabase.rpc("sm_add_note", { p_influencer_id: influencerId, p_note: note });
    return { error: err(error) };
  },

  // Active Brandripplr staff, for "BR Team" reach-outs.
  async getStaff() {
    const { data, error } = await supabase
      .from("users")
      .select("id, first_name, last_name, email")
      .eq("user_type", "staff")
      .eq("deactivate", false)
      .order("first_name");
    if (error) return { staff: [], error: err(error) };
    return { staff: (data || []).map((u) => ({ id: u.id, name: fullName(u) })), error: null };
  },

  // ----------------------------------------------------------------- members
  async getMembers() {
    const { data, error } = await supabase
      .from("sm_member")
      .select("user_id, is_admin, is_lead, created_at, users!sm_member_user_id_fkey(id, first_name, last_name, email, avatar)");
    if (error) return { members: [], error: err(error) };
    const members = (data || [])
      .map((m) => ({
        user_id: m.user_id,
        is_admin: m.is_admin,
        is_lead: m.is_lead,
        name: fullName(m.users),
        email: m.users?.email || "",
        avatar: avatarUrl(m.users?.avatar),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    return { members, error: null };
  },
  async searchStaff(query, excludeIds = []) {
    const term = (query || "").trim().replace(/[%,()]/g, " ");
    if (!term) return { users: [], error: null };
    const { data, error } = await supabase
      .from("users")
      .select("id, first_name, last_name, email")
      .eq("user_type", "staff")
      .eq("deactivate", false)
      .or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%,email.ilike.%${term}%`)
      .limit(8);
    if (error) return { users: [], error: err(error) };
    return { users: (data || []).filter((u) => !excludeIds.includes(u.id)).map((u) => ({ ...u, name: fullName(u) })), error: null };
  },
  async addMember(userId, { isAdmin = false, isLead = true } = {}) {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("sm_member")
      .insert({ user_id: userId, is_admin: isAdmin, is_lead: isLead, added_by: auth?.user?.id || null });
    if (error) return { error: error.code === "23505" ? "Already a member" : err(error) };
    return { error: null };
  },
  async updateMember(userId, patch) {
    const row = {};
    if ("is_admin" in patch) row.is_admin = !!patch.is_admin;
    if ("is_lead" in patch) row.is_lead = !!patch.is_lead;
    const { error } = await supabase.from("sm_member").update(row).eq("user_id", userId);
    return { error: err(error) };
  },
  async removeMember(userId) {
    const { error } = await supabase.from("sm_member").delete().eq("user_id", userId);
    return { error: err(error) };
  },
};
